import { useCallback, useEffect, useReducer } from "react";
import { useIsSmartEdit } from "~/app/config/pageMetadataContext";
import { useSessionState } from "~/app/providers/SessionProvider";
import {
  advanceMergeCartFlow,
  initialMergeCartFlow,
  isActiveMergeCartFlow,
  type ActiveMergeCartFlowPhase,
} from "~/domains/cart/mergeCartFlow";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { MergeCartDialog } from "~/features/bulkAddToCart/components/MergeCartDialog";
import { useGetStateQuery } from "~/features/assistedService/redux/asmApi";

export interface MergeCartReadiness {
  asmActive: boolean;
  asmSynchronized: boolean;
  authenticated: boolean;
  inSmartedit: boolean;
  mergeSourceCartGuid?: string;
  pathname: string;
  punchoutActive: boolean;
  sessionSynchronized: boolean;
  standardShellReady: boolean;
}

export const MergeCustomerCart = ({ standardShellReady }: { standardShellReady: boolean }) => {
  const session = useSessionState();
  const isInSmartEdit = useIsSmartEdit();
  const asm = useGetStateQuery();
  const mergeSourceCartGuid = session.data?.mergeSourceCart?.guid;
  const ready = isMergeCartReady({
    asmActive: Boolean(asm.data?.launched || asm.data?.agent || asm.data?.emulatedUser),
    asmSynchronized: asm.isSuccess && !asm.isFetching,
    authenticated: userIsLoggedIn(),
    inSmartedit: isInSmartEdit,
    mergeSourceCartGuid,
    pathname: window.location.pathname,
    punchoutActive: Boolean(session.data?.commerceContext.punchoutMode),
    sessionSynchronized: session.synchronized,
    standardShellReady,
  });
  const [flow, dispatchFlow] = useReducer(advanceMergeCartFlow, initialMergeCartFlow);

  useEffect(() => {
    if (session.availability === "duplicateInvariant") {
      dispatchFlow({ type: "invalidated" });
      return;
    }
    dispatchFlow({
      type: "sessionObserved",
      ...(ready && mergeSourceCartGuid ? { sourceCartGuid: mergeSourceCartGuid } : {}),
      synchronized: session.synchronized,
    });
  }, [mergeSourceCartGuid, ready, session.availability, session.synchronized]);

  const advanceFlow = useCallback((phase: ActiveMergeCartFlowPhase) => {
    dispatchFlow({ type: "advanced", phase });
  }, []);
  const closeFlow = useCallback(() => {
    dispatchFlow({ type: "closed" });
  }, []);

  if (!isActiveMergeCartFlow(flow)) {
    return null;
  }

  return (
    <MergeCartDialog
      flow={{
        sourceCartGuid: flow.sourceCartGuid,
        onLifecycleChange: advanceFlow,
      }}
      onClose={closeFlow}
    />
  );
};

/**
 * Positive readiness gate for the first ordinary page after authentication/account setup.
 *
 * A successful synchronized Session Data projection proves that the backend accepted the complete
 * identity/account tuple and authorized a nonempty source. HeaderNavigation supplies the standard
 * shell signal. Existing SmartEdit, punchout, and ASM state sources close the remaining special-flow
 * gaps; unknown/loading state is deliberately ineligible.
 */
export function isMergeCartReady(readiness: MergeCartReadiness): boolean {
  return (
    readiness.authenticated &&
    readiness.sessionSynchronized &&
    readiness.standardShellReady &&
    readiness.asmSynchronized &&
    !readiness.asmActive &&
    !readiness.inSmartedit &&
    !readiness.punchoutActive &&
    Boolean(readiness.mergeSourceCartGuid) &&
    !isMergeTransitionRoute(readiness.pathname)
  );
}

function isMergeTransitionRoute(pathname: string): boolean {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);
  const has = (...values: string[]) => values.some((value) => segments.includes(value));

  if (
    has(
      "login",
      "logout",
      "register",
      "registered",
      "registration",
      "selectaccount",
      "training-campus",
      "lms",
      "punchout",
      "oci",
      "cxml",
      "checkout",
      "payment",
      "orderconfirmation",
      "order-confirmation",
      "error",
      "servererror",
      "notfound",
      "maintenance",
      "datasheet",
    )
  ) {
    return true;
  }

  return (
    (segments.includes("static") && segments.includes("welcome")) ||
    (segments.includes("session") && segments.includes("activate"))
  );
}
